/**
 * PeerReviewTemplatesPage — Standalone HR template management page.
 *
 * Lists ALL peer review templates. HR can:
 *   • Create a new template (name + topics + questions) independently of any cycle.
 *   • Edit any existing template (accordion-based topic/question builder).
 *   • Delete a template.
 *
 * Each template shows which cycle it is currently assigned to (if any).
 * Template assignment to a cycle is handled from the Cycles page (Create/Edit cycle dialog).
 */

import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDispatch } from "react-redux";

import PageTitle from "../../../components/Page Component/PageTitle";
import { deleteData, fetchData, postData, putData } from "../../../store/actions";
import { useCustomToast } from "../../../components/Methods/SnackBar";
import {
  peerReviewTemplatesUrl,
  peerReviewTemplateDetailUrl,
} from "../../../Service/Apis";

// ── Types ────────────────────────────────────────────────────────────────────

interface QuestionForm {
  id?: string;
  text: string;
  question_type: "likert" | "yes_no" | "text";
  likert_scale: number | null;
  order: number;
}

interface TopicForm {
  id?: string;
  name: string;
  order: number;
  questions: QuestionForm[];
}

interface CycleInfo {
  id: string;
  name: string;
  status: "draft" | "active" | "closed";
}

interface TemplateListItem {
  id: string;
  name: string;
  /** Number of currently active cycles using this template */
  active_cycle_count: number;
  /** Total number of cycles that reference this template */
  assigned_cycle_count: number;
  /** Cycle name + status pairs for tooltip */
  assigned_cycles_info: CycleInfo[];
  created_by_name: string;
  created_at: string;
  topics: Array<{
    id: string;
    topic_name: string;
    order: number;
    questions: Array<{
      id: string;
      question_text: string;
      question_type: "likert" | "yes_no" | "text";
      likert_scale: number | null;
      order: number;
    }>;
  }>;
}

const emptyQuestion = (order: number): QuestionForm => ({
  text: "",
  question_type: "likert",
  likert_scale: 5,
  order,
});

const emptyTopic = (order: number): TopicForm => ({
  name: "",
  order,
  questions: [emptyQuestion(1)],
});

// ── CycleChips — shows active / other chips with a rich tooltip ──────────────

const STATUS_DOT: Record<CycleInfo["status"], { color: string; label: string }> = {
  active: { color: "#2e7d32", label: "Active" },
  draft:  { color: "#9e9e9e", label: "Draft" },
  closed: { color: "#c62828", label: "Closed" },
};

const CycleTooltipContent: React.FC<{ cycles: CycleInfo[] }> = ({ cycles }) => (
  <Box sx={{ p: 0.5, minWidth: 180 }}>
    {cycles.map((c) => {
      const dot = STATUS_DOT[c.status];
      return (
        <Box key={c.id} display="flex" alignItems="center" gap={1} sx={{ py: 0.4 }}>
          <Box
            component="span"
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: dot.color,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" sx={{ lineHeight: 1.4 }}>
            {c.name}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

interface CycleChipsProps {
  activeCount: number;
  otherCount: number;
  /** Detailed cycle info for tooltips — may be absent on older API responses */
  cyclesInfo?: CycleInfo[];
}

const CycleChips: React.FC<CycleChipsProps> = ({ activeCount, otherCount, cyclesInfo = [] }) => {
  const activeCycles = cyclesInfo.filter((c) => c.status === "active");
  const otherCycles  = cyclesInfo.filter((c) => c.status !== "active");

  const activeTooltip = activeCycles.length > 0
    ? <CycleTooltipContent cycles={activeCycles} />
    : `${activeCount} active cycle${activeCount !== 1 ? "s" : ""}`;

  const otherTooltip = otherCycles.length > 0
    ? <CycleTooltipContent cycles={otherCycles} />
    : `${otherCount} draft/closed cycle${otherCount !== 1 ? "s" : ""}`;

  return (
    <Box display="flex" gap={0.75} flexWrap="wrap" alignItems="center">
      {activeCount > 0 && (
        <Tooltip title={activeTooltip} arrow placement="top">
          <Chip
            label={`${activeCount} active`}
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontWeight: 500, cursor: "default" }}
          />
        </Tooltip>
      )}
      {otherCount > 0 && (
        <Tooltip title={otherTooltip} arrow placement="top">
          <Chip
            label={`${otherCount} other`}
            size="small"
            color="default"
            variant="outlined"
            sx={{ fontWeight: 500, cursor: "default" }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

// ── Main component ───────────────────────────────────────────────────────────

const PeerReviewTemplatesPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const showToast = useCustomToast();

  const [templates, setTemplates] = useState<TemplateListItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Create / Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TemplateListItem | null>(null);
  const [templateName, setTemplateName] = useState("");
  const [topics, setTopics] = useState<TopicForm[]>([emptyTopic(1)]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<TemplateListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchTemplates = () => {
    setLoading(true);
    dispatch(fetchData(`${peerReviewTemplatesUrl}?page_size=100`))
      .then((res: any) => {
        if (res) setTemplates(res.results ?? res);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Dialog helpers ──────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditingTemplate(null);
    setTemplateName("");
    setTopics([emptyTopic(1)]);
    setFormError("");
    setDialogOpen(true);
  };

  const openEdit = (tpl: TemplateListItem) => {
    if (tpl.active_cycle_count > 0) {
      showToast(
        `Cannot edit "${tpl.name}" — it is currently used in ${tpl.active_cycle_count} active cycle${tpl.active_cycle_count !== 1 ? "s" : ""}. Close those cycles first.`,
        "error",
        false
      );
      return;
    }
    setEditingTemplate(tpl);
    setTemplateName(tpl.name);
    // Rebuild topics form from existing data
    const loadedTopics: TopicForm[] = (tpl.topics ?? []).map((t: any, ti: number) => ({
      id: t.id,
      name: t.topic_name,
      order: t.order ?? ti + 1,
      questions: (t.questions ?? []).map((q: any, qi: number) => ({
        id: q.id,
        text: q.question_text,
        question_type: q.question_type,
        likert_scale: q.likert_scale ?? null,
        order: q.order ?? qi + 1,
      })),
    }));
    setTopics(loadedTopics.length > 0 ? loadedTopics : [emptyTopic(1)]);
    setFormError("");
    setDialogOpen(true);
  };

  // ── Topic / question mutations ──────────────────────────────────────────────

  const addTopic = () =>
    setTopics((prev) => [...prev, emptyTopic(prev.length + 1)]);

  const removeTopic = (ti: number) =>
    setTopics((prev) => prev.filter((_, i) => i !== ti));

  const updateTopic = (ti: number, field: keyof TopicForm, value: any) =>
    setTopics((prev) =>
      prev.map((t, i) => (i === ti ? { ...t, [field]: value } : t))
    );

  const addQuestion = (ti: number) =>
    setTopics((prev) =>
      prev.map((t, i) =>
        i === ti
          ? { ...t, questions: [...t.questions, emptyQuestion(t.questions.length + 1)] }
          : t
      )
    );

  const removeQuestion = (ti: number, qi: number) =>
    setTopics((prev) =>
      prev.map((t, i) =>
        i === ti
          ? { ...t, questions: t.questions.filter((_, j) => j !== qi) }
          : t
      )
    );

  const updateQuestion = (ti: number, qi: number, field: keyof QuestionForm, value: any) =>
    setTopics((prev) =>
      prev.map((t, i) =>
        i === ti
          ? {
              ...t,
              questions: t.questions.map((q, j) =>
                j === qi
                  ? {
                      ...q,
                      [field]: value,
                      ...(field === "question_type"
                        ? { likert_scale: value === "likert" ? 5 : null }
                        : {}),
                    }
                  : q
              ),
            }
          : t
      )
    );

  // ── Save ────────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!templateName.trim()) {
      setFormError("Template name is required.");
      return;
    }
    if (topics.some((t) => !t.name.trim())) {
      setFormError("All topic names are required.");
      return;
    }
    setSaving(true);
    setFormError("");

    const payload = {
      name: templateName.trim(),
      topics: topics.map((t, ti) => ({
        ...(t.id ? { id: t.id } : {}),
        topic_name: t.name.trim(),
        order: ti + 1,
        questions: t.questions.map((q, qi) => ({
          ...(q.id ? { id: q.id } : {}),
          question_text: q.text.trim(),
          question_type: q.question_type,
          likert_scale: q.question_type === "likert" ? (q.likert_scale ?? 5) : null,
          order: qi + 1,
        })),
      })),
    };

    try {
      if (editingTemplate) {
        await dispatch(putData(peerReviewTemplateDetailUrl(editingTemplate.id), payload));
        showToast("Template updated.", "success", false);
      } else {
        await dispatch(postData(peerReviewTemplatesUrl, payload));
        showToast("Template created.", "success", false);
      }
      setDialogOpen(false);
      fetchTemplates();
    } catch {
      setFormError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await dispatch(deleteData(peerReviewTemplateDetailUrl(deleteTarget.id)));
      showToast("Template deleted.", "success", false);
      setDeleteTarget(null);
      fetchTemplates();
    } catch {
      showToast("Failed to delete template.", "error", false);
    } finally {
      setDeleting(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Container maxWidth="lg" sx={{ mt: 10, mb: 6 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <PageTitle>Peer Review Templates</PageTitle>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New Template
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : templates.length === 0 ? (
        <Alert severity="info">
          No templates yet. Create one to reuse question sets across review cycles.
        </Alert>
      ) : (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  background: "#1E7B44",
                  "& .MuiTableCell-head": { color: "white", fontWeight: 700, fontSize: "0.82rem", py: 1.5 },
                }}
              >
                <TableCell>Template Name</TableCell>
                <TableCell>Topics & Questions</TableCell>
                <TableCell>In-Use Cycles</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((tpl, idx) => {
                const totalQuestions = tpl.topics?.reduce((sum, t) => sum + (t.questions?.length ?? 0), 0) ?? 0;
                return (
                  <TableRow
                    key={tpl.id}
                    hover
                    sx={{
                      bgcolor: idx % 2 === 0 ? "white" : "grey.50",
                      borderLeft: 4,
                      borderColor: tpl.active_cycle_count > 0 ? "success.main" : tpl.assigned_cycle_count > 0 ? "info.main" : "grey.300",
                      transition: "background 0.15s",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" fontWeight="600">
                        {tpl.name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Box display="flex" gap={0.75} flexWrap="wrap" alignItems="center">
                        <Chip
                          label={`${tpl.topics?.length ?? 0} topic${(tpl.topics?.length ?? 0) !== 1 ? "s" : ""}`}
                          size="small"
                          color="default"
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                        />
                        <Chip
                          label={`${totalQuestions} question${totalQuestions !== 1 ? "s" : ""}`}
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      {tpl.assigned_cycle_count === 0 ? (
                        <Typography variant="caption" color="text.disabled" fontStyle="italic">
                          Unassigned
                        </Typography>
                      ) : (
                        <CycleChips
                          activeCount={tpl.active_cycle_count}
                          otherCount={tpl.assigned_cycle_count - tpl.active_cycle_count}
                          cyclesInfo={tpl.assigned_cycles_info}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {tpl.created_by_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.5, whiteSpace: "nowrap" }}>
                      <Tooltip title={tpl.active_cycle_count > 0 ? "Cannot edit — template is in use by active cycle(s)" : "Edit template"}>
                        <span>
                          <IconButton size="small" onClick={() => openEdit(tpl)} disabled={tpl.active_cycle_count > 0}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={tpl.active_cycle_count > 0 ? "Cannot delete — template is in use by active cycle(s)" : "Delete template"}>
                        <span>
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(tpl)} disabled={tpl.active_cycle_count > 0}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Create / Edit Dialog ───────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTemplate ? `Edit: ${editingTemplate.name}` : "New Peer Review Template"}
        </DialogTitle>
        <DialogContent dividers>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

          <TextField
            label="Template Name"
            fullWidth
            required
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1" fontWeight="medium">Topics</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addTopic}>
              Add Topic
            </Button>
          </Box>

          {topics.map((topic, ti) => (
            <Accordion key={ti} defaultExpanded sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1} width="100%">
                  <Typography variant="body2" fontWeight="medium" sx={{ flex: 1 }}>
                    {topic.name || `Topic ${ti + 1}`}
                  </Typography>
                  <Chip
                    label={`${topic.questions.length} Q`}
                    size="small"
                    variant="outlined"
                    sx={{ mr: 1 }}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => { e.stopPropagation(); removeTopic(ti); }}
                    disabled={topics.length === 1}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  label="Topic Name"
                  fullWidth
                  required
                  size="small"
                  value={topic.name}
                  onChange={(e) => updateTopic(ti, "name", e.target.value)}
                  sx={{ mb: 2 }}
                />

                {topic.questions.map((q, qi) => (
                  <Paper key={qi} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                    <Box display="flex" gap={1} alignItems="flex-start">
                      <TextField
                        label={`Question ${qi + 1}`}
                        size="small"
                        fullWidth
                        value={q.text}
                        onChange={(e) => updateQuestion(ti, qi, "text", e.target.value)}
                      />
                      <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={q.question_type}
                          label="Type"
                          onChange={(e) =>
                            updateQuestion(ti, qi, "question_type", e.target.value)
                          }
                        >
                          <MenuItem value="likert">Likert</MenuItem>
                          <MenuItem value="yes_no">Yes / No</MenuItem>
                          <MenuItem value="text">Text Field</MenuItem>
                        </Select>
                      </FormControl>
                      {q.question_type === "likert" && (
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                          <InputLabel>Scale</InputLabel>
                          <Select
                            value={q.likert_scale ?? 5}
                            label="Scale"
                            onChange={(e) =>
                              updateQuestion(ti, qi, "likert_scale", Number(e.target.value))
                            }
                          >
                            <MenuItem value={5}>1–5</MenuItem>
                            <MenuItem value={10}>1–10</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeQuestion(ti, qi)}
                        disabled={topic.questions.length === 1}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}

                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addQuestion(ti)}
                  sx={{ mt: 0.5 }}
                >
                  Add Question
                </Button>
              </AccordionDetails>
            </Accordion>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : editingTemplate ? "Save Changes" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm ─────────────────────────────────────────────────────── */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          {deleteTarget && deleteTarget.active_cycle_count > 0 ? (
            <Alert severity="error">
              <strong>{deleteTarget.name}</strong> cannot be deleted because it is currently
              used in <strong>{deleteTarget.active_cycle_count} active cycle{deleteTarget.active_cycle_count !== 1 ? "s" : ""}</strong>.
              Close those cycles first before deleting this template.
            </Alert>
          ) : (
            <Typography>
              Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
              {deleteTarget && deleteTarget.assigned_cycle_count > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  This template is referenced by {deleteTarget.assigned_cycle_count} cycle{deleteTarget.assigned_cycle_count !== 1 ? "s" : ""}.
                  Deleting it will remove it from those cycles' history.
                </Alert>
              )}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          {deleteTarget && deleteTarget.active_cycle_count === 0 && (
            <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
              {deleting ? <CircularProgress size={20} /> : "Delete"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PeerReviewTemplatesPage;
